import React, { useState } from "react";
import Navbar from "../components/Navbar";
import Contact from "../components/Contact";
import Breadcrumb from "../components/Breadcrumb";
import MarineSparesAdvantages from "../components/MarineSparesAdvantages";
import useSEO from "../hooks/useSEO";
import { SITE } from "../constants/siteConfig";
import { COLORS, TYPOGRAPHY, SPACING, RADIUS, SHADOWS, TRANSITIONS, GRADIENTS, GLASSMORPHISM, HERO_HEIGHTS } from '../constants/designTokens';

const subItems = [
    "Contactors, Thermal Overloads & Relays",
    "MCCBs, MCBs, ACBs & Circuit Breakers",
    "Limit Switches, Timers & Push Buttons",
    "Frequency Inverters (VFD) & Soft Starters",
    "Automatic Voltage Regulators (AVR)",
    "Marine Cables, Fuses, & Busbars"
];

const faqs = [
    {
        question: "Are your circuit breakers and contactors compatible with my existing switchboard?",
        answer:
            "We cross-reference part numbers, ratings, and physical dimensions against your existing switchboard configuration before quoting, so replacement MCCBs, MCBs, ACBs, and contactors fit and perform as an exact or approved equivalent match. If you provide the equipment nameplate details or original part number, our technical team will confirm compatibility prior to shipment."
    },
    {
        question: "Do your electrical spares carry marine-grade certification?",
        answer:
            "Yes. All electrical spares we supply, including contactors, circuit breakers, relays, and AVRs, are sourced through certified channels with full documentation such as certificates of conformity, type-approval certificates, and manufacturer test reports, ensuring compliance with SOLAS, IMO, and classification society requirements (DNV, Lloyd's Register, ABS, BV)."
    },
    {
        question: "Can you source Variable Frequency Drives (VFDs) and soft starters for specific vessel systems?",
        answer:
            "Absolutely. We regularly source VFDs and soft starters from Schneider, ABB, and Siemens for applications including pumps, fans, and auxiliary motor control systems. Our procurement team can match drive ratings, voltage classes, and communication protocols to your existing automation and control architecture."
    },
    {
        question: "We need an obsolete or discontinued circuit breaker model. Can you find it?",
        answer:
            "Sourcing obsolete or discontinued breakers is one of our core strengths. Through our network of manufacturer contacts, authorized distributors, and specialist brokers, we locate legacy MCCBs, MCBs, and ACBs for older switchboards, including models no longer listed by the original manufacturer, and supply suitable equivalents where a direct replacement is unavailable."
    },
    {
        question: "What are typical lead times for electrical spare parts?",
        answer:
            "Standard electrical spares such as contactors, thermal overloads, relays, and busbars are typically available for shipment within a few business days from stock or authorized distributors. Custom or obsolete items may require additional sourcing time. For urgent requirements, we offer expedited shipping and can usually arrange delivery to major ports within 24-72 hours."
    }
];

const ElectricalSparesFAQ = () => {
    const [openIndex, setOpenIndex] = useState(null);

    const toggleFAQ = idx => {
        setOpenIndex(openIndex === idx ? null : idx);
    };

    return (
        <>
            <style>{`
                .faq-section h2 {
                    color: ${COLORS.primary.dark};
                    font-weight: ${TYPOGRAPHY.fontWeight.bold};
                    text-align: center;
                    margin-bottom: ${SPACING.md};
                }
                .faq-section .section-subtitle {
                    color: ${COLORS.text.primary};
                    font-size: ${TYPOGRAPHY.fontSize.base};
                    text-align: center;
                    max-width: 600px;
                    margin: 0 auto ${SPACING['2xl']};
                }
                .faq-card {
                    border-radius: ${RADIUS.lg};
                    box-shadow: ${SHADOWS.sm};
                    margin-bottom: ${SPACING.md};
                    overflow: hidden;
                    border: 1px solid ${COLORS.ui.border};
                    transition: all 0.3s ease;
                }
                .faq-card:hover {
                    box-shadow: ${SHADOWS.md};
                }
                .faq-header {
                    background: ${COLORS.background.lightBlue};
                    cursor: pointer;
                    padding: ${SPACING.lg} ${SPACING.xl};
                    transition: all 0.3s ease;
                }
                .faq-header:hover {
                    background: ${COLORS.background.lightTeal};
                }
                .faq-header h5 {
                    color: ${COLORS.primary.teal};
                    font-weight: ${TYPOGRAPHY.fontWeight.semibold};
                    font-size: ${TYPOGRAPHY.fontSize.md};
                    margin: 0;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: ${SPACING.md};
                }
                .faq-header h5 span {
                    flex-shrink: 0;
                    font-weight: ${TYPOGRAPHY.fontWeight.normal};
                    font-size: 1.5rem;
                    line-height: 1;
                    transition: transform 0.3s ease;
                }
                .faq-card.open .faq-header h5 span {
                    transform: rotate(45deg);
                }
                .faq-body {
                    color: ${COLORS.text.primary};
                    font-size: ${TYPOGRAPHY.fontSize.base};
                    background: ${COLORS.background.lightTeal};
                    padding: ${SPACING.lg} ${SPACING.xl};
                    line-height: ${TYPOGRAPHY.lineHeight.relaxed};
                    border-top: 1px solid rgba(64, 168, 196, 0.2);
                }

                @media (max-width: 767.98px) {
                    .faq-header {
                        min-height: 60px;
                        display: flex;
                        align-items: center;
                        padding: ${SPACING.md} ${SPACING.lg};
                    }
                    .faq-header h5 {
                        font-size: 1rem !important;
                        line-height: 1.4;
                    }
                    .faq-body {
                        font-size: 0.95rem !important;
                        padding: ${SPACING.md} ${SPACING.lg};
                    }
                }

                @media (max-width: 480px) {
                    .faq-header h5 {
                        font-size: 0.95rem !important;
                    }
                    .faq-body {
                        font-size: 0.9rem !important;
                    }
                }
            `}</style>
            <section className="faq-section py-5" style={{ background: COLORS.background.white }}>
                <div className="container">
                    <h2>Frequently Asked Questions</h2>
                    <p className="section-subtitle">
                        Find answers to common questions about our marine electrical spares sourcing and supply services.
                    </p>
                    <div className="accordion" id="electricalSparesFAQ">
                        {faqs.map((faq, idx) => (
                            <div
                                className={`faq-card ${openIndex === idx ? 'open' : ''}`}
                                key={idx}
                            >
                                <div
                                    className="faq-header"
                                    onClick={() => toggleFAQ(idx)}
                                >
                                    <h5>
                                        {faq.question}
                                        <span>{openIndex === idx ? "−" : "+"}</span>
                                    </h5>
                                </div>
                                {openIndex === idx && (
                                    <div className="faq-body">
                                        {faq.answer}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
};

const ElectricalSpares = () => {
    useSEO({
        title: 'Marine Electrical Spares',
        description: 'Marine-approved electrical spares for power management and distribution: contactors, MCCBs, MCBs, ACBs, circuit breakers, VFDs, soft starters, and AVRs from Schneider, ABB, Siemens, and Terasaki.',
        canonical: '/electrical-spares',
        ogImage: 'https://marine-sealink.com/img/electrical-spares.jpg',
        breadcrumb: [
            { name: 'Home', url: '/' },
            { name: 'Electrical Spares', url: '/electrical-spares' }
        ],
        faqs
    });

    const whatsappMessage = encodeURIComponent(
        "Hello, I need assistance with marine electrical spares."
    );

    return (
        <div className="page-transition">
            <Breadcrumb items={[{ label: 'Home', href: '/' }, { label: 'Electrical Spares', href: '/electrical-spares' }]} />
            <style>{`
                .electrical-spares-hero {
                    background: ${GRADIENTS.overlay}, url('/img/electrical-spares.jpg') center center/cover no-repeat;
                }

                @media (max-width: 767.98px) {
                    .electrical-spares-hero > div {
                        max-width: 90% !important;
                        padding: 2rem 1.5rem !important;
                    }
                    .electrical-spares-hero h1 {
                        font-size: 1.75rem !important;
                    }
                    .electrical-spares-hero p {
                        font-size: 1rem !important;
                    }
                    .category-detail-card {
                        padding: 1.5rem !important;
                    }
                    .cta-section h2 {
                        font-size: 1.5rem !important;
                    }
                    .cta-buttons {
                        flex-direction: column !important;
                        gap: 1rem !important;
                    }
                    .cta-btn {
                        width: 100% !important;
                        justify-content: center !important;
                    }
                }

                @media (max-width: 480px) {
                    .electrical-spares-hero > div {
                        padding: 1.5rem 1rem !important;
                    }
                }

                .category-detail-card {
                    background: ${COLORS.background.white};
                    border-radius: ${RADIUS.xl};
                    padding: ${SPACING['2xl']};
                    box-shadow: ${SHADOWS.md};
                    border: 1px solid ${COLORS.ui.border};
                }

                .category-items {
                    list-style: none;
                    padding: 0;
                    margin: ${SPACING.lg} 0;
                }

                .category-items li {
                    padding: ${SPACING.sm} 0;
                    color: ${COLORS.text.primary};
                    font-size: ${TYPOGRAPHY.fontSize.base};
                    display: flex;
                    align-items: center;
                    gap: ${SPACING.sm};
                }

                .category-items li i {
                    color: ${COLORS.primary.teal};
                    font-size: ${TYPOGRAPHY.fontSize.md};
                    flex-shrink: 0;
                }

                .brands-tag {
                    background: ${COLORS.background.lightTeal};
                    border-radius: ${RADIUS.lg};
                    padding: ${SPACING.sm} ${SPACING.md};
                    font-size: ${TYPOGRAPHY.fontSize.sm};
                    color: ${COLORS.primary.dark};
                    display: inline-block;
                    margin-top: ${SPACING.md};
                }

                .brands-tag strong {
                    color: ${COLORS.primary.teal};
                }

                .cta-section {
                    background: linear-gradient(135deg, ${COLORS.primary.dark} 0%, #1a3a4f 50%, ${COLORS.primary.teal} 100%);
                    position: relative;
                    overflow: hidden;
                }

                .cta-section::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
                    opacity: 0.5;
                }

                .cta-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: ${SPACING.sm};
                    padding: ${SPACING.md} ${SPACING.xl};
                    border-radius: ${RADIUS.lg};
                    font-weight: ${TYPOGRAPHY.fontWeight.semibold};
                    font-size: ${TYPOGRAPHY.fontSize.base};
                    text-decoration: none;
                    transition: ${TRANSITIONS.all};
                    border: 2px solid transparent;
                }

                .cta-btn-primary {
                    background: ${COLORS.background.white};
                    color: ${COLORS.primary.dark};
                    box-shadow: 0 4px 20px rgba(255, 255, 255, 0.2);
                }

                .cta-btn-primary:hover {
                    background: ${COLORS.primary.teal};
                    color: ${COLORS.text.white};
                    transform: translateY(-3px);
                    box-shadow: 0 8px 30px rgba(64, 168, 196, 0.4);
                }

                .cta-btn-whatsapp {
                    background: ${COLORS.ui.whatsapp};
                    color: ${COLORS.text.white};
                    box-shadow: 0 4px 20px rgba(37, 211, 102, 0.3);
                }

                .cta-btn-whatsapp:hover {
                    background: ${COLORS.ui.whatsappHover};
                    color: ${COLORS.text.white};
                    transform: translateY(-3px);
                    box-shadow: 0 8px 30px rgba(37, 211, 102, 0.5);
                }
            `}</style>
            <Navbar />

            {/* Hero Section with image background + dark overlay */}
            <section
                className="electrical-spares-hero d-flex align-items-center justify-content-center"
                style={{
                    minHeight: HERO_HEIGHTS.page,
                    position: "relative",
                    color: COLORS.text.white,
                    textAlign: "center"
                }}
            >
                <div
                    style={{
                        background: GLASSMORPHISM.strong.background,
                        backdropFilter: GLASSMORPHISM.strong.backdropFilter,
                        WebkitBackdropFilter: GLASSMORPHISM.strong.WebkitBackdropFilter,
                        padding: `${SPACING['2xl']} ${SPACING.xl}`,
                        borderRadius: RADIUS['2xl'],
                        boxShadow: GLASSMORPHISM.strong.boxShadow,
                        border: GLASSMORPHISM.strong.border,
                        maxWidth: '700px',
                        margin: "0 auto",
                        animation: 'fadeInUp 0.8s ease-out'
                    }}
                >
                    <h1 style={{
                        fontWeight: TYPOGRAPHY.fontWeight.bold,
                        fontSize: TYPOGRAPHY.fontSize['3xl'],
                        marginBottom: SPACING.lg,
                        lineHeight: TYPOGRAPHY.lineHeight.tight
                    }}>Marine Electrical Spares</h1>
                    <p style={{
                        fontSize: TYPOGRAPHY.fontSize.xl,
                        lineHeight: TYPOGRAPHY.lineHeight.relaxed,
                        marginBottom: 0
                    }}>
                        Comprehensive marine-approved electrical components for power management and distribution, sourced and supplied for vessels and offshore platforms worldwide.
                    </p>
                </div>
            </section>

            {/* Category Detail Section */}
            <section className="py-5" style={{ background: COLORS.background.light }}>
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-lg-10">
                            <div className="category-detail-card fade-in-section">
                                <h2 style={{
                                    color: COLORS.primary.dark,
                                    fontWeight: TYPOGRAPHY.fontWeight.bold,
                                    fontSize: TYPOGRAPHY.fontSize['2xl'],
                                    marginBottom: SPACING.md
                                }}>
                                    Electrical Spares
                                </h2>
                                <p style={{
                                    color: COLORS.text.primary,
                                    fontSize: TYPOGRAPHY.fontSize.lg,
                                    lineHeight: TYPOGRAPHY.lineHeight.relaxed
                                }}>
                                    Comprehensive marine-approved electrical components for power management and distribution.
                                </p>
                                <ul className="category-items">
                                    {subItems.map((item, idx) => (
                                        <li key={idx}>
                                            <i className="fa-solid fa-circle-check"></i>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                                <div className="brands-tag">
                                    <strong>Supported Brands:</strong> Schneider, ABB, Siemens, Terasaki
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="cta-section py-5">
                <div className="container position-relative" style={{ zIndex: 1 }}>
                    <div className="text-center" style={{ color: COLORS.text.white }}>
                        <h2 style={{
                            fontWeight: TYPOGRAPHY.fontWeight.bold,
                            fontSize: TYPOGRAPHY.fontSize['2xl'],
                            marginBottom: SPACING.md
                        }}>
                            Need Electrical Spares? Get a Quote Today
                        </h2>
                        <p style={{
                            fontSize: TYPOGRAPHY.fontSize.lg,
                            marginBottom: SPACING.xl,
                            maxWidth: '600px',
                            margin: '0 auto',
                            opacity: 0.9,
                            lineHeight: TYPOGRAPHY.lineHeight.relaxed,
                            paddingBottom: SPACING.xl
                        }}>
                            From contactors and circuit breakers to VFDs and AVRs, our team sources and delivers marine-approved electrical spares with fast, reliable service worldwide.
                        </p>
                        <div className="cta-buttons d-flex justify-content-center gap-3 flex-wrap">
                            <a href="#contact" className="cta-btn cta-btn-primary">
                                <i className="fa-solid fa-file-invoice"></i>
                                Request a Quote
                            </a>
                            <a
                                href={`${SITE.whatsapp}?text=${whatsappMessage}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="cta-btn cta-btn-whatsapp"
                            >
                                <i className="fa-brands fa-whatsapp"></i>
                                WhatsApp Us
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            <div className="fade-in-section">
                <MarineSparesAdvantages />
            </div>

            <div className="fade-in-section">
                <ElectricalSparesFAQ />
            </div>
            <div className="fade-in-section">
                <Contact />
            </div>
        </div>
    );
};

export default ElectricalSpares;
